import type { RepositoryIntegration, RepositoryType, Tool } from '../../utils/api-client/api-schemas'
import { convertToolToIntegrationType } from '../../utils/repository-utils.tsx'

/** GitLab tools that derive integration status from the repository SCM type, not from RepositoryIntegration records. */
export type GitLabTool = 'gitlab_sast' | 'gitlab_dependency_scanning'

export type ToolIntegrationStatus = 'configured' | 'none'

/**
 * Determines the integration status for a tool.
 * GitLab tools are "configured" when the repository is a GitLab repo (integration is at the SCM level).
 * Other tools check the repository integrations list.
 */
export const getToolIntegrationStatus = (
  tool: Tool,
  integrations: RepositoryIntegration[],
  repositoryType?: RepositoryType
): ToolIntegrationStatus => {
  if (isGitLabTool(tool)) {
    return repositoryType === 'gitlab' ? 'configured' : 'none'
  }
  const integrationType = convertToolToIntegrationType(tool)
  if (!integrationType) {
    return 'none'
  }
  const hasIntegration = integrations.some(i => i.type === integrationType)
  return hasIntegration ? 'configured' : 'none'
}

/**
 * Workflow path for a selected tool.
 * - 'upload-only': no integration, user must upload a file
 * - 'import-only': integration exists but tool only supports import
 * - 'choice': integration exists and tool supports both upload and import
 */
export type WorkflowPath = 'upload-only' | 'import-only' | 'choice'

/**
 * Tools the platform can import findings from without an uploaded file.
 *
 * Mirrors the `NATIVE_TOOLS` constant in
 * `pixee-platform-rest/src/main/java/ai/pixee/platform/api/scans/ValidCreateScanRequest.java`.
 * The platform rejects a fileless scan for any tool outside that set with
 * "Native integration is not supported for tool <tool>", so offering the import
 * option for one of them can only ever produce an error.
 *
 * There is no API that exposes this list (see https://github.com/pixee/product-os/pull/79
 * for why), so the two must be kept in sync by hand: a tool gaining or losing a
 * native integration on the backend requires the same edit here.
 */
const NATIVE_TOOLS: ReadonlySet<Tool> = new Set([
  'arnica_sast',
  'checkmarx',
  'codeql',
  'datadog_sast',
  'dependabot',
  'gitlab_dependency_scanning',
  'gitlab_sast',
  'polaris_sast',
  'semgrep',
  'snyk',
  'sonar',
  'veracode',
])

/**
 * Determines the workflow path for a selected tool.
 * Returns undefined when no tool is selected.
 *
 * Tools with a native integration get the upload/import choice; every other tool
 * is upload-only, because the platform requires a file for them.
 */
export function resolveWorkflowPath(tool: Tool | undefined): WorkflowPath | undefined {
  if (!tool) {
    return undefined
  }
  return NATIVE_TOOLS.has(tool) ? 'choice' : 'upload-only'
}

/**
 * Whether a tool is a GitLab pipeline tool that derives integration status from the repository SCM type.
 */
export const isGitLabTool = (tool: Tool): tool is GitLabTool =>
  tool === 'gitlab_sast' || tool === 'gitlab_dependency_scanning'

/**
 * Validates a commit SHA is a 7-40 character hexadecimal string.
 * Returns an error message if invalid, undefined if valid or empty.
 */
export const validateCommitSha = (sha: string): string | undefined => {
  if (sha === '') return undefined
  if (!/^[0-9a-fA-F]{7,40}$/.test(sha)) {
    return 'Commit hash must be a 7-40 character hexadecimal string'
  }
  return undefined
}
